/**
 * /client/set-password?token=...
 *
 * No CRM authentication of any kind -- the token is the sole access
 * control (project-brain/pages/client-set-password.md). Standalone, no nav
 * chrome, same posture as /sign/:token.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import bbhLogoUrl from '@/assets/bbh-logo.svg';
import './ClientLogin.css';

const MIN_PASSWORD_LENGTH = 8;

type LoadState = 'loading' | 'valid' | 'invalid';

const ClientSetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadState('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/client/auth/set-password/${encodeURIComponent(token)}`);
        const json = (await res.json()) as { success: boolean; data?: { valid: boolean } };
        if (cancelled) return;
        setLoadState(json.success && json.data?.valid ? 'valid' : 'invalid');
      } catch {
        if (!cancelled) setLoadState('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/client/auth/set-password/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: password }),
      });
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string };
      if (!res.ok || !json.success) {
        setError(json.message ?? 'This link is no longer valid.');
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
      setTimeout(() => {
        navigate('/client/login', { state: { message: 'Password set — sign in below' }, replace: true });
      }, 1200);
    } catch {
      setError('Network error. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="client-login-page">
      <div className="client-login-card">
        <div className="client-login-header">
          <img className="client-login-logo" src={bbhLogoUrl} alt="" />
          <h1 className="client-login-title">Set your password</h1>
        </div>

        {loadState === 'loading' && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--spacing-lg) 0' }}>
            <LoadingSpinner />
          </div>
        )}

        {loadState === 'invalid' && (
          <div className="client-login-error" role="alert">
            This link is no longer valid. Please contact your Backed by Humans contact for a new one.
          </div>
        )}

        {loadState === 'valid' && submitted && (
          <p className="client-login-subtitle" style={{ textAlign: 'center' }}>
            Password set — redirecting to sign in…
          </p>
        )}

        {loadState === 'valid' && !submitted && (
          <form className="client-login-form" onSubmit={handleSubmit} noValidate>
            {error && (
              <div className="client-login-error" role="alert">
                {error}
              </div>
            )}

            <div className="client-login-field">
              <label htmlFor="password" className="client-login-label">
                New password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                disabled={submitting}
              />
            </div>

            <div className="client-login-field">
              <label htmlFor="confirm-password" className="client-login-label">
                Confirm password
              </label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            <Button type="submit" variant="primary" size="lg" disabled={submitting} className="client-login-submit">
              {submitting ? 'Setting password…' : 'Set password'}
            </Button>
          </form>
        )}

        {loadState !== 'loading' && (
          <p className="client-login-subtitle" style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
            <Link to="/client/login">Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default ClientSetPassword;
